import React from "react";
import Autocomplete from "@mui/material/Autocomplete";
import TextField from "@mui/material/TextField";
import MuiCheckbox from "@mui/material/Checkbox";
import FormControlLabel from "@mui/material/FormControlLabel";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import dayjs from "dayjs";
import { useValidationError, emailPattern } from "./validation";
import { formatPhoneDigits } from "./data";
import { colors } from "../../utils/styles";
import { fmtDate } from "../../utils/dateFormat";

const formFieldMinHeight = 42;
const formFieldFontSize = 14;

export function SectionHeader({ number, title, subtitle }) {
  return (
    <div style={{ marginBottom: 20, paddingBottom: 12, borderBottom: `2px solid ${colors.gold}` }}>
      <h2
        style={{
          margin: 0,
          fontSize: 18,
          fontWeight: 700,
          color: colors.navy,
          letterSpacing: "-0.02em"
        }}
      >
        {number}. {title}
      </h2>
      {subtitle && <p style={{ margin: "2px 0 0", fontSize: 12, color: colors.textMuted }}>{subtitle}</p>}
    </div>
  );
}

export function SectionSubheader({ title }) {
  return <span style={{ fontWeight: 600, color: colors.slate }}>{title}</span>;
}

export function FieldHelpText({ children }) {
  return <p style={{ margin: "4px 0 0", fontSize: 11, color: colors.textLight }}>{children}</p>;
}

export function CharCount({ value, max }) {
  const length = (value || "").length;
  const atLimit = length >= max;
  return (
    <p style={{ margin: "4px 0 0", fontSize: 11, color: atLimit ? colors.error : colors.textLight }}>
      {length}/{max} characters
      {atLimit && <span style={{ fontWeight: 600 }}> - limit reached</span>}
    </p>
  );
}

// Flex column with justify-content: end keeps the control aligned to the bottom of its
// grid cell when adjacent labels wrap to different heights. `input` is the field's own
// control; `extras` are caller-supplied children rendered last (e.g. char counters).
//
// `path` opts the field into ValidationContext: when an error matches, the
// shell stamps `data-error-path` for the page-level scroll-to-first lookup and
// renders the validation message under the field. A caller-supplied
// `errorMessage` (e.g. EmailField's touched-state pattern check) wins, falling
// back to the validation message — so a field's own pre-Submit errors keep
// working.
function FieldShell({ id, label, required, errorMessage, helpText, extras, path, children }) {
  const validationError = useValidationError(path);
  const displayedError = errorMessage || validationError?.message || null;
  return (
    <div style={{ minWidth: 0 }} data-error-path={validationError ? path : undefined}>
      {label && (
        <label
          htmlFor={id}
          style={{
            display: "block",
            fontSize: 12,
            fontWeight: 600,
            color: colors.textMuted,
            marginBottom: 6,
            textTransform: "uppercase",
            letterSpacing: "0.04em"
          }}
        >
          {label} {required && <span style={{ color: colors.error }}>*</span>}
        </label>
      )}
      {children}
      {displayedError && (
        <p style={{ margin: "4px 0 0", fontSize: 11, color: colors.error }}>{displayedError}</p>
      )}
      {helpText && <FieldHelpText>{helpText}</FieldHelpText>}
      {extras}
    </div>
  );
}

export function ReadOnlyField({ label, required, helpText, value, accent, children }) {
  const id = React.useId();
  const text = value || "";
  const textareaRef = React.useRef(null);
  // Resize the textarea to fit its content. Re-runs on value change AND on width
  // change (window resize, container resize) via ResizeObserver.
  React.useLayoutEffect(() => {
    const el = textareaRef.current;
    if (!el) return;
    const fit = () => {
      el.style.height = "auto";
      el.style.height = `${el.scrollHeight}px`;
    };
    fit();
    // Defer the observer's fit() to the next frame so the height write doesn't
    // happen inside the observer callback — that synchronous write is what
    // triggers the "ResizeObserver loop completed with undelivered notifications" warning.
    let frame = 0;
    const observer = new ResizeObserver(() => {
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(fit);
    });
    observer.observe(el);
    return () => {
      cancelAnimationFrame(frame);
      observer.disconnect();
    };
  }, [text]);
  return (
    <FieldShell id={id} label={label} required={required} helpText={helpText} extras={children}>
      <textarea
        id={id}
        ref={textareaRef}
        readOnly
        value={text}
        rows={1}
        placeholder="—"
        style={{
          display: "block",
          width: "100%",
          padding: "10px 14px",
          background: accent ? colors.goldPale : colors.sectionBg,
          border: `1px solid ${accent ? colors.goldLight : colors.borderLight}`,
          borderRadius: 6,
          fontSize: formFieldFontSize,
          color: colors.text,
          minHeight: formFieldMinHeight,
          lineHeight: 1.5,
          resize: "none",
          outline: "none",
          overflow: "hidden",
          boxSizing: "border-box",
          cursor: "default"
        }}
      />
    </FieldShell>
  );
}

function highlightMatch(text, query) {
  if (!query) return text;
  const lowerText = text.toLowerCase();
  const lowerQuery = query.toLowerCase();
  const segments = [];
  let cursor = 0;
  let index = lowerText.indexOf(lowerQuery, cursor);
  while (index !== -1) {
    if (index > cursor) {
      segments.push({ text: text.slice(cursor, index), match: false });
    }
    segments.push({ text: text.slice(index, index + query.length), match: true });
    cursor = index + query.length;
    index = lowerText.indexOf(lowerQuery, cursor);
  }
  if (cursor < text.length) {
    segments.push({ text: text.slice(cursor), match: false });
  }
  if (segments.length === 0) return text;
  return (
    <span style={{ display: "inline" }}>
      {segments.map((segment, i) =>
        segment.match ? (
          <strong key={i} style={{ fontWeight: 700 }}>
            {segment.text}
          </strong>
        ) : (
          <React.Fragment key={i}>{segment.text}</React.Fragment>
        )
      )}
    </span>
  );
}

export function SelectField({
  label,
  required,
  helpText,
  value,
  onChange,
  options,
  placeholder,
  path,
  disabled,
  children
}) {
  const id = React.useId();
  const [inputValue, setInputValue] = React.useState("");
  const selectedOption = options.find((o) => o.value === value) || null;
  const validationError = useValidationError(path);
  return (
    <FieldShell id={id} label={label} required={required} helpText={helpText} path={path} extras={children}>
      <Autocomplete
        disabled={disabled}
        value={selectedOption}
        onChange={(_event, next) => {
          onChange({ target: { value: next ? next.value : "" } });
        }}
        inputValue={inputValue}
        onInputChange={(_event, next) => setInputValue(next)}
        options={options}
        getOptionLabel={(option) => option.label || ""}
        isOptionEqualToValue={(option, val) => option.value === val.value}
        renderOption={(props, option) => {
          const { key, ...rest } = props;
          return (
            <li key={key} {...rest}>
              {highlightMatch(option.label || "", inputValue)}
            </li>
          );
        }}
        fullWidth
        autoHighlight
        openOnFocus
        slotProps={{
          popper: { sx: { minWidth: { xs: 200, sm: 250 } } },
          paper: {
            sx: {
              "& .MuiAutocomplete-option": {
                fontSize: formFieldFontSize,
                whiteSpace: "normal",
                wordBreak: "break-word",
                lineHeight: 1.4
              }
            }
          },
          listbox: { sx: { maxHeight: 320 } }
        }}
        renderInput={(params) => (
          <TextField
            {...params}
            placeholder={placeholder || "Select..."}
            error={Boolean(validationError)}
            inputProps={{ ...params.inputProps, id }}
          />
        )}
      />
    </FieldShell>
  );
}

export function InputField({
  label,
  required,
  helpText,
  value,
  onChange,
  onBlur,
  placeholder,
  type = "text",
  inputMode,
  errorMessage,
  multiline,
  minRows,
  maxLength,
  path,
  disabled,
  children
}) {
  const id = React.useId();
  const validationError = useValidationError(path);
  const hasError = Boolean(errorMessage) || Boolean(validationError);
  return (
    <FieldShell
      id={id}
      label={label}
      required={required}
      errorMessage={errorMessage}
      helpText={helpText}
      path={path}
      extras={children}
    >
      <TextField
        type={type}
        value={value}
        onChange={onChange}
        onBlur={onBlur}
        placeholder={placeholder}
        error={hasError}
        multiline={multiline}
        minRows={minRows}
        fullWidth
        disabled={disabled}
        slotProps={{ htmlInput: { id, inputMode, maxLength } }}
        sx={multiline ? { "& textarea": { marginBottom: "-20px" } } : undefined}
      />
    </FieldShell>
  );
}

// State stores raw 10-digit string ("9095551234"); display masks as "(909) 555-1234".
export function PhoneField({ label, required, helpText, value, onChange, path, children }) {
  const id = React.useId();
  const formatted = formatPhoneDigits(value || "");
  const validationError = useValidationError(path);
  const handleChange = (event) => {
    const digits = event.target.value.replace(/\D/g, "").slice(0, 10);
    onChange({ target: { value: digits } });
  };
  return (
    <FieldShell id={id} label={label} required={required} helpText={helpText} path={path} extras={children}>
      <TextField
        type="tel"
        value={formatted}
        onChange={handleChange}
        placeholder="(___) ___-____"
        error={Boolean(validationError)}
        fullWidth
        slotProps={{ htmlInput: { id, inputMode: "tel", maxLength: 14 } }}
      />
    </FieldShell>
  );
}

export function PhoneExtensionField({ label, required, helpText, value, onChange, path, children }) {
  const id = React.useId();
  const validationError = useValidationError(path);
  const handleChange = (event) => {
    const digits = event.target.value.replace(/\D/g, "").slice(0, 10);
    onChange({ target: { value: digits } });
  };
  return (
    <FieldShell id={id} label={label} required={required} helpText={helpText} path={path} extras={children}>
      <TextField
        type="tel"
        value={value || ""}
        onChange={handleChange}
        placeholder="10 digits max"
        error={Boolean(validationError)}
        fullWidth
        slotProps={{ htmlInput: { id, inputMode: "numeric", maxLength: 10 } }}
      />
    </FieldShell>
  );
}

// State stores raw digit string ("250000"); display adds commas and optional prefix.
export function NumberField({
  label,
  required,
  helpText,
  value,
  onChange,
  placeholder,
  prefix = "",
  path,
  children
}) {
  const handleChange = (event) => {
    const digits = event.target.value.replace(/\D/g, "");
    onChange({ target: { value: digits } });
  };
  const formatted = value ? Number(value).toLocaleString("en-US") : "";
  const displayValue = formatted && prefix ? `${prefix}${formatted}` : formatted;
  return (
    <InputField
      label={label}
      required={required}
      helpText={helpText}
      value={displayValue}
      onChange={handleChange}
      placeholder={placeholder}
      type="text"
      inputMode="numeric"
      path={path}
    >
      {children}
    </InputField>
  );
}

export function EmailField({
  label,
  required,
  helpText,
  value,
  onChange,
  placeholder,
  maxLength,
  path,
  children
}) {
  const [touched, setTouched] = React.useState(false);
  const showError = touched && value && !emailPattern.test(value);
  return (
    <InputField
      label={label}
      required={required}
      helpText={helpText}
      value={value}
      onChange={onChange}
      onBlur={() => setTouched(true)}
      placeholder={placeholder || "email@agency.gov"}
      type="email"
      inputMode="email"
      maxLength={maxLength}
      errorMessage={showError ? "Enter a valid email address." : null}
      path={path}
    >
      {children}
    </InputField>
  );
}

export function DateField({ label, required, helpText, value, onChange, path, children }) {
  const id = React.useId();
  const dateValue = value ? dayjs(value) : null;
  const validationError = useValidationError(path);

  const handleChange = (newDate) => {
    if (!newDate || !newDate.isValid()) {
      onChange({ target: { value: "" } });
      return;
    }
    onChange({ target: { value: newDate.format("YYYY-MM-DD") } });
  };

  return (
    <FieldShell id={id} label={label} required={required} helpText={helpText} path={path} extras={children}>
      <LocalizationProvider dateAdapter={AdapterDayjs}>
        <DatePicker
          value={dateValue}
          onChange={handleChange}
          slotProps={{
            textField: { id, fullWidth: true, error: Boolean(validationError) }
          }}
        />
      </LocalizationProvider>
    </FieldShell>
  );
}

export function Checkbox({ fieldId, label, checked, onChange, disabled }) {
  return (
    <FormControlLabel
      htmlFor={fieldId}
      control={<MuiCheckbox id={fieldId} checked={checked} onChange={onChange} disabled={disabled} />}
      label={label}
      disabled={disabled}
    />
  );
}

const infoNoteVariants = {
  default: {
    background: colors.infoBg,
    borderColor: colors.border,
    iconColor: colors.navy,
    textColor: colors.text,
    Icon: InfoOutlinedIcon
  },
  success: {
    background: colors.successBg,
    borderColor: colors.success,
    iconColor: colors.success,
    textColor: colors.text,
    Icon: CheckCircleOutlineIcon
  },
  error: {
    background: colors.errorBg,
    borderColor: colors.error,
    iconColor: colors.error,
    textColor: colors.error,
    Icon: ErrorOutlineIcon
  },
  warning: {
    background: colors.warningBg,
    borderColor: colors.warning,
    iconColor: colors.warning,
    textColor: colors.text,
    Icon: WarningAmberIcon
  }
};

export function InfoNote({ children, variant = "default", icon = true }) {
  const { background, borderColor, iconColor, textColor, Icon } =
    infoNoteVariants[variant] || infoNoteVariants.default;

  return (
    <div
      style={{
        padding: "10px 14px",
        background,
        border: icon ? `1px solid ${borderColor}` : "none",
        borderRadius: 6,
        display: "flex",
        alignItems: "flex-start",
        gap: icon ? 10 : 0
      }}
      role="note"
    >
      {icon && <Icon sx={{ fontSize: 18, color: iconColor, flexShrink: 0, marginTop: "1px" }} />}
      <div
        style={{
          margin: 0,
          fontSize: 13,
          color: textColor,
          lineHeight: 1.5,
          minWidth: 0,
          overflowWrap: "anywhere"
        }}
      >
        {children}
      </div>
    </div>
  );
}

export function PriorSubmissionBanner({ submission }) {
  if (!submission) return null;
  const first = submission.formPreparerFirstName || "";
  const last = submission.formPreparerLastName || "";
  const name = `${first} ${last}`.trim() || "a previous preparer";
  const submittedAt = fmtDate(submission.submittedAt);
  return (
    <InfoNote variant="success">
      A form has been submitted by <strong>{name}</strong> on <strong>{submittedAt}</strong>. Editable fields
      display previous submission information but can be updated.
    </InfoNote>
  );
}
